import parentLogger from "./logger/logger";
import {
  codeAtom,
  enableDividerInsertionAtom,
  enableMessageInsertionAtom,
  enableMultiThemeAtom,
  enableParticipantInsertionAtom,
  enableParticipantStyleEditingAtom,
  enableScopedThemingAtom,
  lifelineReadyAtom,
  modeAtom,
  onContentChangeAtom,
  onEventEmitAtom,
  onThemeChangeAtom,
  renderingReadyAtom,
  RenderMode,
  stickyOffsetAtom,
  themeAtom,
  verticalModeAtom,
} from "./store/Store";
import { DiagramFrame } from "./components/DiagramFrame/DiagramFrame";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { VERSION } from "./version.ts";
import * as htmlToImage from "html-to-image";
import Parser from "./parser/index.js";

import "./assets/tailwind.css";
import "./assets/tailwind-preflight.less";
import "./components/Cosmetic.scss";
import "./components/Cosmetic-blue.scss";
import "./components/Cosmetic-black-white.scss";
import "./components/Cosmetic-star-uml.scss";
import "./components/theme-blue-river.scss";
import "./themes/theme-dark.css";

import { getStartTime, calculateCostTime } from "./utils/CostTime";
import { clearCache } from "./utils/RenderingCache";
import { createRoot } from "react-dom/client";
import { StrictMode } from "react";
import { createStore, Provider } from "jotai";
import { SeqDiagram } from "./components/DiagramFrame/SeqDiagram/SeqDiagram.tsx";
export { renderToSvg } from "./svg/renderToSvg";
export type { RenderResult, RenderOptions } from "./svg/renderToSvg";
const logger = parentLogger.child({ name: "core" });

interface Config {
  theme?: string;
  enableScopedTheming?: boolean;
  onThemeChange?: (data: { theme: string; scoped?: boolean }) => void;
  enableMultiTheme?: boolean;
  stickyOffset?: number | false;
  onContentChange?: (code: string) => void;
  onEventEmit?: (name: string, data: unknown) => void;
  mode?: RenderMode;
  // Editing feature flags. Default off; opt in per surface.
  // Message insertion is currently broken for nested messages, so the flag
  // exists to ship participant insertion independently.
  enableParticipantInsertion?: boolean;
  enableMessageInsertion?: boolean;
  enableDividerInsertion?: boolean;
  enableParticipantStyleEditing?: boolean;
}

export interface ParseResult {
  pass: boolean;
  errorDetails: ErrorDetail[];
}
export interface  ErrorDetail {
  line: number;
  column: number;
  msg: string;
}


interface IZenUml {
  get code(): string | undefined;
  get theme(): string | undefined;
  parse(text: string): Promise<ParseResult>;
  // Resolve after rendering is finished.
  render(
    code: string | undefined,
    config: Config | undefined,
  ): Promise<IZenUml>;
}

export default class ZenUml implements IZenUml {
  static readonly version = VERSION;
  // UMD Compatibility Layer - Support both window.zenuml and window.zenuml.default
  // The problem was introduced at commit 4c46879f when we remove the named export VueSequence
  static readonly default = ZenUml; // Self-reference for UMD compatibility

  private readonly el: HTMLElement;
  private _code: string | undefined;
  private _theme: string | undefined;
  private readonly store: ReturnType<typeof createStore>;
  private _currentTimeout: NodeJS.Timeout | undefined;
  private _lastRenderingCostMilliseconds = 0;
  private initialRender = true;
  constructor(el: HTMLElement | string, naked: boolean = false) {
    this.el = typeof el === "string" ? document.querySelector(el)! : el;
    this.store = createStore();

    this.store.sub(themeAtom, () => {
      this.store.get(onThemeChangeAtom)({
        theme: this.store.get(themeAtom),
        scoped: this.store.get(enableScopedThemingAtom),
      });
    });

    createRoot(this.el).render(
      <StrictMode>
        <Provider store={this.store}>
          {/* IMPORTANT: The .zenuml class here works with Tailwind's important: ".zenuml" configuration.
              With this setup, Tailwind generates selectors like ".zenuml .bg-skin-canvas" instead of
              just ".bg-skin-canvas". This means all Tailwind utilities used in child components
              (like DiagramFrame) will only work when they are descendants of an element with the
              .zenuml class. This provides scoped styling for the ZenUML library. */}
          <div className="zenuml">
            <ErrorBoundary>
              {naked ? <SeqDiagram /> : <DiagramFrame />}
            </ErrorBoundary>
          </div>
        </Provider>
      </StrictMode>,
    );
    this.addPortalRootElement();
  }

  // DON'T REMOVE: headlessui portal root hack
  // ref: https://github.com/tailwindlabs/headlessui/discussions/666#discussioncomment-4966117
  private addPortalRootElement(): void {
    const portalRootElement = document.createElement("div");
    portalRootElement.id = "headlessui-portal-root";
    portalRootElement.className = "zenuml";
    portalRootElement.append(document.createElement("div"));
    document.body.append(portalRootElement);
  }

  setVerticalMode(mode: "html" | "legacy") {
    this.store.set(verticalModeAtom, mode);
  }

  async render(
    code: string | undefined,
    config: Config | undefined,
  ): Promise<IZenUml> {
    if (this._currentTimeout) {
      logger.debug("rendering clearTimeout");
      clearTimeout(this._currentTimeout);
    }
    logger.debug("rendering", code, config);
    this._code = code === undefined ? this._code : code;
    this._theme = config?.theme || this._theme;
    this.store.set(stickyOffsetAtom, config?.stickyOffset ?? 0);
    this.store.set(themeAtom, this._theme || "default");
    this.store.set(
      enableScopedThemingAtom,
      config?.enableScopedTheming || false,
    );
    this.store.set(modeAtom, config?.mode || RenderMode.Dynamic);

    // this.initialRender is used to avoid the first rendering is debounced by setTimeout.
    // The first rendering should be executed immediately. It fixes the issue that causes the blank screen on mermaid live editor.
    if (this.initialRender) {
      this.initialRender = false;
      await this.doRender(config);
    } else {
      this._currentTimeout = setTimeout(
        async () => await this.doRender(config),
        this.calculateDebounceMilliseconds(),
      );
    }

    return this;
  }

  async doRender(config: Config | undefined) {
    logger.debug("rendering start");
    const start = getStartTime();
    clearCache();
    this.store.set(onContentChangeAtom, config?.onContentChange || (() => {}));
    this.store.set(onThemeChangeAtom, config?.onThemeChange || (() => {}));
    this.store.set(onEventEmitAtom, config?.onEventEmit || (() => {}));
    if (config?.enableMultiTheme !== undefined) {
      this.store.set(enableMultiThemeAtom, config?.enableMultiTheme);
    }
    if (config?.enableParticipantInsertion !== undefined) {
      this.store.set(
        enableParticipantInsertionAtom,
        config.enableParticipantInsertion,
      );
    }
    if (config?.enableMessageInsertion !== undefined) {
      this.store.set(enableMessageInsertionAtom, config.enableMessageInsertion);
    }
    if (config?.enableDividerInsertion !== undefined) {
      this.store.set(enableDividerInsertionAtom, config.enableDividerInsertion);
    }
    if (config?.enableParticipantStyleEditing !== undefined) {
      this.store.set(
        enableParticipantStyleEditingAtom,
        config.enableParticipantStyleEditing,
      );
    }
    if (this._code === this.store.get(codeAtom)) {
      return true;
    } else {
      await new Promise((resolve) => {
        this.store.set(lifelineReadyAtom, []);
        this.store.sub(renderingReadyAtom, () => {
          if (this.store.get(renderingReadyAtom)) {
            resolve(true);
          }
        });
        this.store.set(codeAtom, this._code || "");
      });
    }
    setTimeout(() => {
      this._lastRenderingCostMilliseconds = calculateCostTime(
        "rendering end",
        start,
      );
    }, 0);
  }

  calculateDebounceMilliseconds(): number {
    let debounce = this._lastRenderingCostMilliseconds;
    if (debounce > 2000) debounce = 2000;
    logger.debug("rendering debounce: " + debounce + "ms");
    return debounce;
  }

  get code(): string | undefined {
    return this._code;
  }

  get theme(): string | undefined {
    return this._theme;
  }

  parse(codeOrText: string): Promise<ParseResult> {
    return new Promise((resolve) => {
      try {
        // Clear any previous errors
        Parser.ErrorDetails.length = 0;
        const result = Parser.RootContext(codeOrText);
        logger.debug("errors", Parser.ErrorDetails);
        const errors = [...Parser.ErrorDetails];
        // Clear errors after reading
        Parser.ErrorDetails.length = 0;
        if (errors.length > 0 || result === null) {
          // Return ParseResult indicating failure with structured error info
          const parseResult: ParseResult = {
            pass: false,
            errorDetails: errors,
          };
          resolve(parseResult);
          return;
        }
        const parseResult: ParseResult = {
          pass: true,
          errorDetails: [],
        };
        resolve(parseResult);
      } catch {
        const parseResult: ParseResult = {
          pass: false,
          errorDetails: [],
        };
        resolve(parseResult);
      }
    });
  }

  async getPng(): Promise<string> {
    return htmlToImage.toPng(this.el, {
      backgroundColor: "white",
      filter: (node) => {
        return !node?.classList?.contains("hide-export");
      },
    });
  }

  async getSvg(): Promise<string> {
    return htmlToImage.toSvg(this.el, {
      filter: (node) => {
        return !node?.classList?.contains("hide-export");
      },
    });
  }
}
